import { useEffect, useState } from "react";
import { Bell, BellOff, CheckCircle2, Loader2 } from "lucide-react";
import { AccountLayout } from "@/components/candidate/AccountLayout";
import { apiFetch } from "@/components/candidate/shared";

const inputClass =
    "w-full border border-ink/15 bg-ivory px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink/35 focus:border-forest";

const EMPLOYMENT_TYPES = ["", "Full-time", "Contract", "Part-time", "Temporary"];

export default function CandidateAlerts() {
    const [prefs, setPrefs] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        apiFetch("/candidate/job-alerts")
            .then((r) => (r.ok ? r.json() : null))
            .then(setPrefs)
            .catch(() => {});
    }, []);

    const save = async (enabled) => {
        if (saving) return;
        setSaving(true);
        setSaved(false);
        const body = { ...prefs, enabled: enabled !== undefined ? enabled : prefs.enabled };
        await apiFetch("/candidate/job-alerts", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        setPrefs(body);
        setSaving(false);
        setSaved(true);
    };

    const set = (key) => (e) => setPrefs((p) => ({ ...p, [key]: e.target.value }));

    return (
        <AccountLayout title="Job Alerts" subtitle="Control which new roles reach your inbox.">
            {!prefs ? (
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-forest border-t-transparent" />
            ) : (
                <div className="max-w-2xl" data-testid="alerts-page">
                    <div className="flex items-center justify-between border border-ink/10 bg-sand p-6">
                        <div className="flex items-center gap-4">
                            {prefs.enabled ? (
                                <Bell size={20} strokeWidth={1.5} className="text-forest" />
                            ) : (
                                <BellOff size={20} strokeWidth={1.5} className="text-ink/40" />
                            )}
                            <div>
                                <p className="text-sm font-medium text-ink">
                                    Alerts are {prefs.enabled ? "on" : "off"}
                                </p>
                                <p className="text-xs text-ink/50">
                                    {prefs.enabled
                                        ? "Matching roles are emailed to you as they're published."
                                        : "You won't receive job alert emails."}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            data-testid="alerts-toggle"
                            onClick={() => save(!prefs.enabled)}
                            disabled={saving}
                            className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${
                                prefs.enabled
                                    ? "border border-ink/20 text-ink/60 hover:border-forest hover:text-forest"
                                    : "bg-forest text-ivory hover:bg-forest-deep"
                            }`}
                        >
                            {prefs.enabled ? "Disable" : "Enable"}
                        </button>
                    </div>

                    <div className="mt-10 space-y-5">
                        <p className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-forest">
                            <span className="h-px w-6 bg-champagne" aria-hidden="true" />
                            Alert preferences
                        </p>
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">Profession</span>
                                <input data-testid="alerts-profession" type="text" value={prefs.profession} onChange={set("profession")} placeholder="e.g. nurse" className={inputClass} />
                            </div>
                            <div>
                                <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">Keywords</span>
                                <input data-testid="alerts-keywords" type="text" value={prefs.keywords} onChange={set("keywords")} placeholder="e.g. ICU, offshore" className={inputClass} />
                            </div>
                            <div>
                                <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">Countries (comma separated)</span>
                                <input data-testid="alerts-countries" type="text" value={prefs.countries} onChange={set("countries")} placeholder="e.g. Saudi Arabia, Canada" className={inputClass} />
                            </div>
                            <div>
                                <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">Industries (comma separated)</span>
                                <input data-testid="alerts-industries" type="text" value={prefs.industries} onChange={set("industries")} placeholder="e.g. Healthcare" className={inputClass} />
                            </div>
                            <div>
                                <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-ink/55">Employment type</span>
                                <select data-testid="alerts-employment" value={prefs.employment_type} onChange={set("employment_type")} className={`${inputClass} cursor-pointer`}>
                                    {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t || "Any type"}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                type="button"
                                data-testid="alerts-save-btn"
                                onClick={() => save()}
                                disabled={saving}
                                className="inline-flex items-center gap-2 bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.14em] text-ivory transition-colors hover:bg-forest-deep disabled:opacity-70"
                            >
                                {saving && <Loader2 size={14} className="animate-spin" />}
                                Save Preferences
                            </button>
                            {saved && (
                                <span data-testid="alerts-saved" className="inline-flex items-center gap-1.5 text-sm text-forest">
                                    <CheckCircle2 size={15} strokeWidth={1.5} />
                                    Saved
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </AccountLayout>
    );
}
